// Authenticated fetch core for the tickets booking tool.
//
// SINGLE backend. This used to be a two-backend client — ticketing on wayq-transit
// plus account identity (/api/v1/users/me) on weebee, via a second `weebeeRequest`
// helper. Both the weebee helper and the App Check header are gone: the app now
// authenticates against the transit Firebase project, so a weebee call would 401,
// and transit leaves APP_CHECK_ENABLED unset so the header was already ignored.
//
// Auth is a Firebase ID token from the transit project; transit's tenantauth
// resolves its issuer to the `wayq` tenant.
import { auth, config, getCommonHeaders } from '@/lib/platform';

async function authHeaders(contentType: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': contentType,
    ...getCommonHeaders(),
  };
  const user = auth.currentUser;
  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }
  return headers;
}

async function throwFromResponse(res: Response, verb: string): Promise<never> {
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = undefined;
  }
  if (payload && typeof payload === 'object') {
    throw payload; // RFC 7807 problem — works with isApiError/getErrorMessage/isHttpStatus
  }
  throw {
    type: 'about:blank',
    status: res.status,
    title: res.statusText,
    detail: `${verb} failed (${res.status})`,
  };
}

/** Ticketing request → wayq-transit (the app's only backend). */
export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${config.transitApiUrl}${path}`, {
    method,
    headers: await authHeaders('application/json'),
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) await throwFromResponse(res, 'Request');
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Upload raw image bytes (auth headers, the image's Content-Type as the body type)
 * and return the parsed JSON. Used for IGM issue screenshots, whose endpoint takes
 * a raw body (not JSON) and returns a public URL.
 */
export async function uploadBinary<T>(path: string, file: Blob): Promise<T> {
  const res = await fetch(`${config.transitApiUrl}${path}`, {
    method: 'POST',
    headers: await authHeaders(file.type || 'application/octet-stream'),
    body: file,
  });

  if (!res.ok) await throwFromResponse(res, 'Upload');
  return (await res.json()) as T;
}

/** Percent-encode a path segment (transaction / order / issue id). */
export const q = encodeURIComponent;
